import { FileType } from '../../../polly-manage.constants';
import { WorkspaceContentItem, WorkspaceFile } from '../../interfaces';
import { getFileData } from './workspace-files.utilities';
import {
  MAX_FILE_SIZE_COPY_MOVE_RENAME_OPERATION, MAX_FOLDER_SIZE_COPY_MOVE_RENAME_OPERATION,
} from '../../../modules/workspace-module/polly-manage-workspace.constants';
import {
  FILE_SIZE_EXCEEDS_MESSAGE,
  FOLDER_CONTAINS_LARGE_FILES,
  FOLDER_SIZE_EXCEEDS_MESSAGE,
} from '../../../polly-manage.messages';
import { Hit } from 'polly-commons';

const moveOperations: string[] = ['WORKSPACE_MOVE_IN_PROGRESS', 'WORKSPACE_MOVE_COMPLETE', 'WORKSPACE_MOVE_FAILED'];
const copyOperations: string[] = ['WORKSPACE_COPY_IN_PROGRESS', 'WORKSPACE_COPY_COMPLETE', 'WORKSPACE_COPY_FAILED'];
const completedOperations: string[] = [
  'WORKSPACE_MOVE_IN_PROGRESS',
  'WORKSPACE_MOVE_COMPLETE',
  'WORKSPACE_COPY_IN_PROGRESS',
  'WORKSPACE_COPY_COMPLETE',
  'WORKSPACE_RENAME_COMPLETE',
];
const renameOperations: string[] = ['WORKSPACE_RENAME_COMPLETE', 'WORKSPACE_RENAME_FAILED'];

/**
 * Checks if we have received move operation related action in websocket response
 *
 * @param action received from websocket server
 * @return boolean
 */
export function isMoveOperation(action: string): boolean {
  return moveOperations.includes(action);
}

/**
 * Checks if we have received copy operation related action in websocket response
 *
 * @param action received from websocket server
 * @return boolean
 */
export function isCopyOperation(action: string): boolean {
  return copyOperations.includes(action);
}

/**
 * Checks if we have received rename operation related action in websocket response
 *
 * @param action received from websocket server
 * @return boolean
 */
export function isRenameOperation(action: string): boolean {
  return renameOperations.includes(action);
}

/**
 * Checks if we have received successful files copy/move operation in websocket response
 *
 * @param action received from websocket server
 * @return boolean
 */
export function isFileOperationSuccessful(action: string): boolean {
  return completedOperations.includes(action);
}

/**
 * @description Checks if we are on same path as destination folder to copy/move files
 *
 * @param sourcePath path of source
 * @param destinationPath path of destination
 * @return boolean
 */
export function isSamePath(sourcePath: string | undefined, destinationPath: string | undefined): boolean {
  return `/${sourcePath}` === destinationPath;
}

/**
 * @description Checks if user is trying to move folder within the same folder
 *
 * @param destinationFolderPath Where files will be moved
 * @param selectedWorkspaceItems files/folders to be moved
 * @return boolean
 */
export function checkSameFolderMoveOperation(destinationFolderPath: string, selectedWorkspaceItems: WorkspaceFile[]): boolean {
  let isSamePathMoveOperation: boolean = false;
  selectedWorkspaceItems.forEach((selectedItem) => {
    if (
      selectedItem.originalFile._source.entity_type === FileType.FOLDER
      && destinationFolderPath.startsWith(selectedItem.originalFile._source.s3_key)) {
      isSamePathMoveOperation = true;
      return;
    }
  });
  return isSamePathMoveOperation;
}

/**
 * Accepts file/folder ID and returns the path extracted from it.
 *
 * @param item ID of file/folder
 * @return string
 */
export function getParentFolderPath(item: string): string {
  if (item.endsWith('/')) {
    const folderPath = item.slice(0, item.length - 1);
    return folderPath.substring(0, folderPath.lastIndexOf('/') + 1);
  } else return item.substring(0, item.lastIndexOf('/') + 1);
}

/**
 * For Mixpanel event tracking, get all the selected files names and IDs from array
 *
 * @param selectedItems Needs to be sent in properties of event
 * @return fileNames: string[], fileIds: string[]
 */
export function getFileNamesAndIDs(selectedItems: Hit<WorkspaceContentItem>[]): {fileNames: string[], fileIds: string[]} {
  const fileNames: string[] = [];
  const fileIds: string[] = [];
  selectedItems.map((file: Hit<WorkspaceContentItem>) => {
    const fileData = getFileData(file);
    if (fileData) {
      fileNames.push(fileData.fileName);
      fileIds.push(file._source.s3_key || file._id);
    }
  });
  return {fileNames, fileIds};
}

/**
 * @description Checks if the files/notebooks and folders are exceeding the size limit for copy, move & rename operation
 *
 * @param itemType Type of workspace item - file/folder/notebook
 * @param size Item size
 * @return boolean
 */
export function doesWorkspaceItemExceedSizeLimit(itemType: string, size: number): boolean {
  if ([FileType.FILE, FileType.NOTEBOOK].includes(itemType)) {
    return size > MAX_FILE_SIZE_COPY_MOVE_RENAME_OPERATION;
  } else if (itemType === FileType.FOLDER) {
    return (size > MAX_FOLDER_SIZE_COPY_MOVE_RENAME_OPERATION);
  }
  return false;
}

/**
 * @description Gets preprocessed messages for size limits to be shown to user
 *
 * @param action prepended action before the generic message
 * @return string[]
 */
export function getSizeExceedsMessages(action: string): {
  fileExceedsSizeMessage: string,
  folderExceedsSizeMessage: string,
  folderContainsLargeFilesMessage: string,
} {
  return {
    fileExceedsSizeMessage: `${action} ${FILE_SIZE_EXCEEDS_MESSAGE}`,
    folderExceedsSizeMessage: `${action} ${FOLDER_SIZE_EXCEEDS_MESSAGE}`,
    folderContainsLargeFilesMessage: `${action} ${FOLDER_CONTAINS_LARGE_FILES}`,
  };
}

export function getAnalysisURL(fileContent: WorkspaceContentItem): string {
  return `compute/analysis/${fileContent.analysis_run_id}?workspace_id=${fileContent.workspace_id}`;
}
